import { callExternalApi } from './external-api.service';
import { getLogger } from '../utils/logger';

const logger = getLogger('SubscriptionService');

/** The three plans a claim can resolve to. These are also the `limits` keys the
 *  backend meters against, so anything the UI names a plan has to be one of them. */
export type EffectiveTier = 'free' | 'standard' | 'pro';

const ENTITLED_TIERS: EffectiveTier[] = ['standard', 'pro'];

/** Requires an explicit timezone designator. A bare `2026-07-01T00:00:00` is
 *  read by `new Date` as *local* time, which silently moves a trial deadline by
 *  hours; every writer of this field emits a `Z`-suffixed UTC string. */
const HAS_TIMEZONE = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/**
 * Parse an ISO-8601 UTC timestamp, or null when it cannot be trusted.
 *
 * The claim is free-form JSON written by two producers, so a value that is
 * missing, the wrong type, malformed or timezone-less has to read as "no
 * evidence" rather than throw.
 */
export const parseUtc = (value: unknown): Date | null => {
  if (typeof value !== 'string' || !value || !HAS_TIMEZONE.test(value)) return null;

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * The plan this claim entitles its holder to, judged against `now`.
 *
 * Mirrors the backend resolver exactly — both are pinned to the shared vectors
 * in `backend/tests/fixtures/entitlement_vectors.json`. It has to be computed
 * here too, because nothing ever rewrites the claim when a trial ends: without
 * this the UI shows a running trial forever while the API meters the same user
 * as free.
 *
 * Never denies: the worst a stale, malformed or absent claim can produce is the
 * free plan.
 */
export const effectiveTier = (claim: any, now: Date = new Date()): EffectiveTier => {
  const subscription = claim && typeof claim === 'object' ? claim : {};
  const status = String(subscription.status ?? '').toLowerCase();
  const tier = String(subscription.tier ?? '').toLowerCase() as EffectiveTier;

  // The signup fortnight writes standard, a promotional grant writes pro, and a
  // legacy "trialing" tier clamps to standard — which is what the fortnight
  // always meant.
  const claimedOrStandard = ENTITLED_TIERS.includes(tier) ? tier : 'standard';

  if (status === 'active') return claimedOrStandard;

  const trialEnd = parseUtc(subscription.trial_end_utc);
  if (status === 'trialing' && trialEnd !== null && trialEnd > now) return claimedOrStandard;

  return 'free';
};

export interface SubscriptionInfo {
  status: string;
  /** Paid tier from the Auth0 claim ("standard" / "pro"), null if absent.
   *  Same source as `status`, so anything naming the plan stays consistent with
   *  anything showing the status. */
  tier: string | null;
  /** The plan the claim actually entitles this user to right now. Prefer this
   *  over `tier`/`status` for anything the user reads as "your plan". */
  effectiveTier: EffectiveTier;
  trialEndUtc: string | null;
  trialDays: number; // Calculated from trialEndUtc for display
  isActive: boolean;
  /** A trial *window still open*: status trialing AND the end date in the
   *  future. The status alone stays "trialing" long after the trial ended. */
  isTrialActive: boolean;
  lastUpdated: string | null;
}

export const subscriptionService = {
  /**
   * Calculate remaining trial days from UTC timestamp
   */
  calculateTrialDaysFromUtc: (trialEndUtc: string | null): number => {
    if (!trialEndUtc) return 0;
    
    try {
      const trialEndDate = new Date(trialEndUtc);
      const now = new Date();
      
      if (trialEndDate > now) {
        return Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      }
      
      return 0;
    } catch (error) {
      logger.error('Error calculating trial days from UTC:', error);
      return 0;
    }
  },

  /**
   * Get subscription information from Auth0 custom claims
   */
  getSubscriptionInfo: (user: any): SubscriptionInfo => {
    // Use Auth0 custom claims as single source of truth
    const domain = process.env.REACT_APP_AUTH0_CUSTOM_CLAIMS_DOMAIN;
    const claimNamespace = `https://${domain}/`;
    const subscription = user?.[`${claimNamespace}subscription`];
    
    const status = subscription?.status || 'inactive';
    const trialEndUtc = subscription?.trial_end_utc || null;
    const trialDays = subscriptionService.calculateTrialDaysFromUtc(trialEndUtc);
    const trialEnd = parseUtc(trialEndUtc);

    return {
      status,
      tier: subscription?.tier || null,
      effectiveTier: effectiveTier(subscription),
      trialEndUtc,
      trialDays,
      isActive: status === 'active',
      isTrialActive: status === 'trialing' && trialEnd !== null && trialEnd > new Date(),
      lastUpdated: subscription?.updated_utc || null
    };
  },

  /**
   * Check if the user may use the app at all.
   *
   * Signing in is the whole test: every account holds at least the free plan,
   * and the API bounds it per product (429/403) rather than refusing the
   * session. Reading the claim here instead would lock out exactly the users
   * whose stored claim went stale — an ended trial nobody rewrote — while the
   * API happily served them.
   */
  hasAccess: (user: any): boolean => Boolean(user),

  /**
   * Check if subscription data should be synced (older than 6 hours)
   */
  shouldSyncSubscription: (user: any): boolean => {
    // Check if subscription guards are disabled via environment variable
    const useSub = process.env.REACT_APP_USE_SUB?.toLowerCase()?.trim();
    
    if (useSub === 'false') {
      logger.info('Subscription sync disabled via REACT_APP_USE_SUB environment variable');
      return false;
    } else if (useSub && useSub !== 'true') {
      logger.warn(`Invalid REACT_APP_USE_SUB environment variable value: ${useSub}. Must be 'true' or 'false'. Defaulting to enabled.`);
    }

    // Use Auth0 custom claims as single source of truth
    const domain = process.env.REACT_APP_AUTH0_CUSTOM_CLAIMS_DOMAIN;
    const claimNamespace = `https://${domain}/`;
    const subscription = user?.[`${claimNamespace}subscription`];
    
    if (!subscription?.updated_utc) return true;
    
    const lastUpdate = new Date(subscription.updated_utc);
    const sixHoursAgo = new Date(Date.now() - 6 * 60 * 60 * 1000);
    return lastUpdate < sixHoursAgo;
  },

  /**
   * Sync subscription status from Stripe
   */
  syncSubscriptionStatus: async (token: string) => {
    // Check if subscription guards are disabled via environment variable
    const useSub = process.env.REACT_APP_USE_SUB?.toLowerCase()?.trim();
    
    if (useSub === 'false') {
      logger.info('Subscription sync disabled via REACT_APP_USE_SUB environment variable');
      return { message: 'Subscription sync disabled' };
    } else if (useSub && useSub !== 'true') {
      logger.warn(`Invalid REACT_APP_USE_SUB environment variable value: ${useSub}. Must be 'true' or 'false'. Defaulting to enabled.`);
    }

    const config = {
      url: `${process.env.REACT_APP_API_SERVER_URL}/api/subscription/sync`,
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${token}`,
      },
    };

    const { data, error } = await callExternalApi({ config });

    if (error) {
      throw new Error(error.message);
    }

    return data;
  },

  /**
   * Get Stripe customer portal URL for subscription management
   */
  getCustomerPortalUrl: async (token: string): Promise<string> => {
    // Check if subscription guards are disabled via environment variable
    const useSub = process.env.REACT_APP_USE_SUB?.toLowerCase()?.trim();
    
    if (useSub === 'false') {
      logger.info('Subscription portal disabled via REACT_APP_USE_SUB environment variable');
      throw new Error('Subscription management is disabled');
    } else if (useSub && useSub !== 'true') {
      logger.warn(`Invalid REACT_APP_USE_SUB environment variable value: ${useSub}. Must be 'true' or 'false'. Defaulting to enabled.`);
    }

    const config = {
      url: `${process.env.REACT_APP_API_SERVER_URL}/api/payments/customer-portal`,
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${token}`,
      },
    };

    const { data, error } = await callExternalApi({ config });

    if (error) {
      throw new Error(error.message);
    }

    // The response comes back as a parsed JSON object with url property
    if (data && typeof data === 'object' && 'url' in data) {
      return (data as any).url;
    }

    throw new Error('Invalid response format');
  },

  /**
   * Get Stripe checkout URL for subscription creation
   */
  getSubscriptionCheckoutUrl: async (token: string, tier?: string): Promise<string> => {
    // Check if subscription guards are disabled via environment variable
    const useSub = process.env.REACT_APP_USE_SUB?.toLowerCase()?.trim();

    if (useSub === 'false') {
      logger.info('Subscription checkout disabled via REACT_APP_USE_SUB environment variable');
      throw new Error('Subscription checkout is disabled');
    } else if (useSub && useSub !== 'true') {
      logger.warn(`Invalid REACT_APP_USE_SUB environment variable value: ${useSub}. Must be 'true' or 'false'. Defaulting to enabled.`);
    }

    const config = {
      url: `${process.env.REACT_APP_API_SERVER_URL}/api/payments/create-link`,
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${token}`,
      },
      data: JSON.stringify({ tier: tier || 'standard' }),
    };

    const { data, error } = await callExternalApi({ config });

    if (error) {
      throw new Error(error.message);
    }

    // The response comes back as a parsed JSON object with url property
    if (data && typeof data === 'object' && 'url' in data) {
      return (data as any).url;
    }

    throw new Error('Invalid response format');
  }
};
